import { useQuery } from "@tanstack/react-query";
import { AGENT_ROLES, type AgentRole } from "@paperclip-cloud/shared";
import { agentHostsApi } from "../api/queries/agents";
import { useFleet } from "../hooks/useFleet";
import { StatusDot } from "./StatusDot";

const ROLE_LABEL: Record<AgentRole, string> = {
  ceo: "CEO",
  ops: "Operations",
};

export interface AgentSwitcherProps {
  companyId: string | null;
  active: AgentRole;
  onChange: (role: AgentRole) => void;
}

export function AgentSwitcher({ companyId, active, onChange }: AgentSwitcherProps) {
  const fleet = useFleet(companyId);
  const hosts = useQuery({
    queryKey: ["agent-hosts", companyId],
    queryFn: () => agentHostsApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 10_000,
  });

  const isOnline = (role: AgentRole): boolean => {
    const host = hosts.data?.agent_hosts.find((h) => h.role === role);
    if (!host || !host.enabled || !host.device_id) return false;
    const dev = fleet.data?.devices.find((d) => d.id === host.device_id);
    return !!dev?.online;
  };

  return (
    <div
      role="tablist"
      aria-label="Agent"
      className="flex items-center gap-1 border-b border-border-subtle px-2"
    >
      {AGENT_ROLES.map((role) => {
        const online = isOnline(role);
        const isActive = role === active;
        return (
          <button
            key={role}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onChange(role)}
            className={`flex items-center gap-2 px-3 py-2 text-[12px] font-medium tracking-[-0.005em] border-b-2 -mb-px transition-colors duration-[160ms] ease-standard ${
              isActive
                ? "border-accent text-primary"
                : "border-transparent text-secondary hover:text-primary"
            }`}
          >
            <StatusDot
              tone={online ? "running" : "idle"}
              title={online ? `${ROLE_LABEL[role]} host online` : `${ROLE_LABEL[role]} host offline`}
            />
            <span>{ROLE_LABEL[role]}</span>
          </button>
        );
      })}
    </div>
  );
}
